import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';
import {
  CROSS_PLANT_SCOPE_MARKER,
  type ModulePermission,
  type PermissionAction,
  type PermissionSet,
  type PermissionsResult,
  type PermissionsState,
} from './types';

/**
 * เส้นทางจริงที่ client ต้องเรียก — `docs/API_CONTRACT.md` ระบุว่า business/auth
 * endpoint ในตารางแสดงแบบตัด base prefix แต่ URL ที่ client เรียกต้องขึ้นต้นด้วย
 * `/v1` เสมอ (`GET /me/permissions` → `GET /v1/me/permissions`)
 */
export const ME_PERMISSIONS_PATH = '/v1/me/permissions';

export interface FetchPermissionsOptions {
  /** base path ของ API (default: อ่านจาก `VITE_API_BASE_URL` แบบ same-origin) */
  readonly apiBaseUrl?: string;
  /** รองรับการยกเลิก request เมื่อ component unmount หรือเปลี่ยน user */
  readonly signal?: AbortSignal;
  /**
   * inject ได้เพื่อการทดสอบ
   *
   * ค่าเริ่มต้นคือ `apiFetch` ไม่ใช่ `globalThis.fetch` — header
   * `Authorization: Bearer` และการล้าง session เมื่อเจอ `401` อยู่ที่นั่นจุดเดียว
   * (`lib/auth/authorizedFetch.ts`) client ตัวนี้จึงไม่ต้องรู้จัก token เลย
   */
  readonly fetchImpl?: typeof fetch;
}

function asBoolean(value: unknown): boolean {
  // deny-safe: อะไรที่ไม่ใช่ `true` แท้ๆ ถือว่าไม่มีสิทธิ์
  return value === true;
}

function asNullableString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function asStringArray(value: unknown): readonly string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter((entry): entry is string => typeof entry === 'string' && entry !== '');
}

function parseModulePermission(raw: unknown): ModulePermission | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;
  const moduleCode = record.module_code;
  if (typeof moduleCode !== 'string' || moduleCode.trim() === '') {
    return null;
  }
  return {
    module_code: moduleCode,
    can_create: asBoolean(record.can_create),
    can_view: asBoolean(record.can_view),
    can_edit: asBoolean(record.can_edit),
    can_approve: asBoolean(record.can_approve),
  };
}

/**
 * ตรวจรูปร่าง payload ก่อนเชื่อ — payload ที่ผิดรูปถือเป็นความล้มเหลว
 * ไม่ใช่ "ไม่มีสิทธิ์แบบเงียบๆ" เพื่อให้แยก bug ออกจาก denial ได้
 */
export function parsePermissionSet(raw: unknown): PermissionSet | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const record = raw as Record<string, unknown>;

  const employeeId = record.employee_id;
  if (typeof employeeId !== 'string' || employeeId.trim() === '') {
    return null;
  }

  if (!Array.isArray(record.modules)) {
    return null;
  }

  const modules: ModulePermission[] = [];
  for (const entry of record.modules) {
    const parsed = parseModulePermission(entry);
    if (parsed === null) {
      return null;
    }
    modules.push(parsed);
  }

  return {
    employee_id: employeeId,
    display_name: asNullableString(record.display_name),
    department_name: asNullableString(record.department_name),
    plant_scope: asStringArray(record.plant_scope),
    system_roles: asStringArray(record.system_roles),
    modules,
  };
}

/**
 * เรียก `GET /v1/me/permissions`
 *
 * ฟังก์ชันนี้ **ไม่ throw** — ความล้มเหลวทุกแบบถูกแปลงเป็นสถานะที่ deny-safe
 * เพื่อไม่ให้ผู้เรียกเผลอ `catch` แล้วเดาว่ามีสิทธิ์
 */
export async function fetchPermissions(
  options: FetchPermissionsOptions = {},
): Promise<PermissionsResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const baseUrl = options.apiBaseUrl ?? getConfiguredApiBaseUrl();
  const url = buildApiUrl(baseUrl, ME_PERMISSIONS_PATH);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  if (response.status === 401) {
    return { status: 'unauthenticated' };
  }
  if (response.status === 403) {
    return { status: 'forbidden' };
  }
  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    return { status: 'invalid-response' };
  }

  const permissions = parsePermissionSet(payload);
  if (permissions === null) {
    return { status: 'invalid-response' };
  }

  return { status: 'ready', permissions };
}

function matrixAllows(permissions: PermissionSet, moduleCode: string, action: PermissionAction) {
  const entry = permissions.modules.find((module) => module.module_code === moduleCode);
  if (entry === undefined) {
    return false;
  }
  switch (action) {
    case 'CREATE':
      return entry.can_create;
    case 'VIEW':
      return entry.can_view;
    case 'EDIT':
      return entry.can_edit;
    case 'APPROVE':
      return entry.can_approve;
  }
}

/**
 * ตัดสินว่าจะ "แสดง" ปุ่ม/เมนูหรือไม่ — **UX เท่านั้น ไม่ใช่ด่านความปลอดภัย**
 * ผู้ตัดสินจริงคือ `require_permission` ที่ backend ทุก request เสมอ
 * (`references/authorization-rbac.md` ข้อ 5)
 *
 * อ่านจาก `modules` ที่เซิร์ฟเวอร์ส่งมาเท่านั้น — **ห้าม derive กติกา
 * ADMIN/AUDITOR ซ้ำจาก `system_roles`** `docs/API_CONTRACT.md` ระบุว่า `modules`
 * "คือผลลัพธ์สุดท้ายหลังบังคับ role guard แล้ว ไม่ใช่ dump ดิบ" การคำนวณกติกา
 * เดิมซ้ำที่นี่จะสร้างความจริงชุดที่สองที่ขัดกับเซิร์ฟเวอร์ได้ (เช่นตอน guard
 * ฝั่ง backend เปลี่ยนแล้ว frontend ยังจำกติกาเก่า) ซึ่งเป็นความล้มเหลวที่
 * ประโยคนั้นใน contract เขียนไว้เพื่อป้องกัน — ที่นี่จึง "อ่าน flag ที่ได้รับมา"
 * อย่างเดียว
 *
 * deny-safe แบบไม่มีข้อยกเว้น: ทุกสถานะที่ไม่ใช่ `ready` คืน `false`
 * switch ด้านล่างแจกแจงครบทุกสถานะโดยไม่มี `default` เพื่อให้ TypeScript
 * เป็นผู้พิสูจน์ความครบถ้วน — สถานะใหม่ที่เพิ่มใน `PermissionsState` จะทำให้
 * compile ไม่ผ่านแทนที่จะตกลงไปในกิ่งที่อาจแปลว่า "มีสิทธิ์"
 */
export function can(
  state: PermissionsState,
  moduleCode: string,
  action: PermissionAction,
): boolean {
  switch (state.status) {
    case 'ready':
      return matrixAllows(state.permissions, moduleCode, action);
    case 'loading':
    case 'unauthenticated':
    case 'forbidden':
    case 'network-error':
    case 'server-error':
    case 'invalid-response':
    case 'cancelled':
      return false;
  }
}

/**
 * แปลง `plant_scope` เป็นข้อความที่ผู้ใช้อ่านแล้วไม่เข้าใจผิด
 *
 * `plant_scope` เก็บ `plant.plant_code` แต่ AUDITOR ได้ marker `['ALL']` ตาม
 * cross-plant exception (`docs/API_CONTRACT.md`) ถ้าปล่อยให้ marker ไปแสดงดิบ ๆ
 * ผู้ใช้จะอ่านว่าเป็นโรงงานรหัส "ALL" จึงต้องแปลงเป็นคำอธิบายที่ชัดเจน
 */
export function describePlantScope(plantScope: readonly string[]): string {
  if (plantScope.some((code) => code.toUpperCase() === CROSS_PLANT_SCOPE_MARKER)) {
    return 'ทุกโรงงาน (สิทธิ์ตรวจสอบข้ามโรงงาน)';
  }
  if (plantScope.length === 0) {
    return 'ยังไม่ได้กำหนดโรงงานที่เข้าถึงได้';
  }
  return plantScope.join(', ');
}

/** ข้อความอธิบายสถานะให้ผู้ใช้ — ต้องไม่บอกเป็นนัยว่ามีสิทธิ์ */
export function describePermissionsState(state: PermissionsState): string {
  switch (state.status) {
    case 'loading':
      return 'กำลังตรวจสอบสิทธิ์การใช้งาน…';
    case 'ready':
      return 'ยืนยันสิทธิ์จากเซิร์ฟเวอร์แล้ว';
    case 'unauthenticated':
      return 'ยังไม่ได้เข้าสู่ระบบ — ปิดคำสั่งทั้งหมดไว้ก่อน';
    case 'forbidden':
      return 'บัญชีนี้ไม่มีสิทธิ์เรียกดูข้อมูลสิทธิ์ — ปิดคำสั่งทั้งหมดไว้ก่อน';
    case 'network-error':
      return 'ติดต่อเซิร์ฟเวอร์ไม่ได้ — ปิดคำสั่งทั้งหมดไว้ก่อน';
    case 'server-error':
      return `เซิร์ฟเวอร์ตอบ ${state.httpStatus} — ปิดคำสั่งทั้งหมดไว้ก่อน`;
    case 'invalid-response':
      return 'ข้อมูลสิทธิ์จากเซิร์ฟเวอร์ผิดรูปแบบ — ปิดคำสั่งทั้งหมดไว้ก่อน';
    case 'cancelled':
      return 'ยกเลิกการตรวจสอบสิทธิ์ — ปิดคำสั่งทั้งหมดไว้ก่อน';
  }
}
